import { api } from "@/lib/api";

// A separate connection per selected speaker fixes the direction before audio leaves the iPad.
export function createDeskStream({ target, audio, onEvent, onState, onSoundBlocked }) {
  let closed = false, stream, peer, channel, timer;
  const abort = new AbortController();
  const close = () => {
    closed = true; abort.abort(); clearTimeout(timer);
    stream?.getTracks().forEach(track => track.stop());
    channel?.close(); peer?.close();
    audio.pause(); audio.srcObject = null;
  };
  const ready = (async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia || typeof RTCPeerConnection === "undefined") throw new Error("Use a supported browser with microphone access over HTTPS.");
      onState("connecting");
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      if (closed) { stream.getTracks().forEach(track => track.stop()); return; }
      timer = setTimeout(() => { if (!closed) { close(); onState("interrupted"); } }, 30000);
      const { data } = await api.post("/admin/first-line-desk/session", { target_language: target }, { signal: abort.signal });
      if (closed) return;
      peer = new RTCPeerConnection();
      stream.getAudioTracks().forEach(track => peer.addTrack(track, stream));
      peer.ontrack = event => {
        if (closed) return;
        audio.srcObject = event.streams[0];
        Promise.resolve(audio.play()).catch(() => { if (!closed) onSoundBlocked(); });
      };
      peer.onconnectionstatechange = () => {
        if (closed) return;
        if (peer.connectionState === "connected") onState("live");
        if (["failed", "disconnected", "closed"].includes(peer.connectionState)) { close(); onState("interrupted"); }
      };
      channel = peer.createDataChannel("oai-events");
      channel.onmessage = ({ data: raw }) => {
        if (closed || typeof raw !== "string" || raw.length > 1000000) return;
        try {
          const event = JSON.parse(raw);
          if (event.type === "error") { close(); onState("interrupted"); }
          else onEvent(event);
        } catch { /* Ignore malformed provider events. */ }
      };
      const offer = await peer.createOffer(); await peer.setLocalDescription(offer);
      if (closed) return;
      const response = await fetch("https://api.openai.com/v1/realtime/translations/calls", {
        method: "POST", headers: { Authorization: `Bearer ${data.value}`, "Content-Type": "application/sdp" }, body: offer.sdp, signal: abort.signal,
      });
      if (!response.ok) throw new Error("The live interpreter could not connect. Check API access or try again.");
      const sdp = await response.text();
      if (closed) return;
      await peer.setRemoteDescription({ type: "answer", sdp });
      // Close even a failed handshake; never leave a microphone/session running indefinitely.
      clearTimeout(timer);
      timer = setTimeout(() => { if (!closed) { close(); onState("ended"); } }, 5 * 60000);
    } catch (error) { if (!closed) { close(); throw error; } }
  })();
  return { ready, close, pause: () => { stream?.getAudioTracks().forEach(track => { track.enabled = false; }); onState("paused"); } };
}

export function appendDeskTranscript(previous, event) {
  const field = event.type === "session.input_transcript.delta" ? "source" : event.type === "session.output_transcript.delta" ? "translation" : null;
  if (!field || typeof event.delta !== "string") return previous;
  return { ...previous, [field]: ((previous[field] || "") + event.delta).slice(-8000) };
}
